import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, LucideHelpCircle, X } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { BRAND_LOGO } from "@/lib/assets";
import TrackingLoader from "@/components/app/TrackingLoader";

/**
 * Wraps a funnel step: header (logo, progress) + back-button + main slot + skeleton/loading prep state.
 * `preparing`: shows loading state on first mount before revealing content.
 */
export default function FunnelLayout({
  step,
  totalSteps = 4,
  title,
  subtitle,
  loadingMessages = [
    "מנתח את הנתונים שלך...",
    "מאתר את החוסמים החונקים...",
    "מכין את ההצעה המושלמת עבורך...",
  ],
  children,
  variant = "light", // 'dark' for Guide 1, 'light' otherwise
}) {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [error, setError] = useState(null);
  const [preparing, setPreparing] = useState(true);
  const [progress, setProgress] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/leads/${leadId}/public`);
        setLead(data);
        track("funnel_view", {
          campaignId: data.campaign_id,
          leadId: data.lead_id,
          payload: { step },
        });
      } catch (e) {
        setError("הקישור אינו תקין");
      }
    })();
  }, [leadId, step]);

  useEffect(() => {
    if (!lead) return;
    // Fake progress bar that builds anticipation
    let p = 0;
    const interval = setInterval(() => {
      p += Math.random() * 22 + 8;
      if (p >= 100) {
        p = 100;
        clearInterval(interval);
        setProgress(100);
        setTimeout(() => setPreparing(false), 350);
      } else {
        setProgress(Math.round(p));
      }
    }, 380);
    const msgInt = setInterval(() => {
      setMsgIdx((i) => (i + 1) % loadingMessages.length);
    }, 800);
    return () => {
      clearInterval(interval);
      clearInterval(msgInt);
    };
  }, [lead, loadingMessages.length]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf9] p-6">
        <div className="bg-white rounded-3xl p-8 text-center max-w-sm border border-black/5">
          <X className="w-12 h-12 mx-auto text-brand-negative mb-3" />
          <h2 className="text-xl font-bold mb-2">קישור לא תקין</h2>
          <p className="text-stone-600 mb-4">{error}</p>
          <Link to="/" className="inline-flex h-11 px-6 rounded-full bg-brand-charcoal text-white items-center font-bold">חזרה לדף הראשי</Link>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafaf9]">
        <div className="w-12 h-12 rounded-full border-4 border-brand-amber border-t-transparent animate-spin" />
      </div>
    );
  }

  if (preparing) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-6 ${variant === "dark" ? "bg-brand-stone text-white" : "bg-[#fafaf9] text-brand-charcoal"}`}>
        <div className="max-w-md w-full text-center">
          <img src={BRAND_LOGO} alt="" className={`h-12 mx-auto mb-8 ${variant === "dark" ? "invert" : ""}`} />
          <div className="mb-6">
            <p className="text-lg font-bold mb-1">{loadingMessages[msgIdx]}</p>
            <p className="text-sm opacity-60">מתאים את התוכן עבורך</p>
          </div>
          <div className="w-full h-2 rounded-full bg-stone-300/30 overflow-hidden">
            <div
              className="h-full bg-brand-amber transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-3 text-xs opacity-70">{progress}%</p>
        </div>
      </div>
    );
  }

  const headerBg =
    variant === "dark"
      ? "bg-brand-stone/80 border-white/10"
      : "bg-white/85 border-black/5";
  const headerText = variant === "dark" ? "text-white" : "text-brand-charcoal";

  return (
    <div className={variant === "dark" ? "min-h-screen bg-brand-stone text-white" : "min-h-screen bg-[#fafaf9] text-brand-charcoal"}>
      <TrackingLoader slug={lead.campaign_slug} listId={lead.list_id} />
      <header className={`sticky top-0 z-40 backdrop-blur-md ${headerBg} border-b`}>
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            data-testid="funnel-back-btn"
            className={`flex items-center gap-1 text-sm opacity-70 hover:opacity-100 ${headerText}`}
          >
            <ChevronLeft className="w-4 h-4" />
            חזרה
          </button>
          <img src={BRAND_LOGO} alt="" className={`h-9 ${variant === "dark" ? "invert" : ""}`} />
          <span className={`text-xs ${headerText} opacity-70`}>
            שלב {step}/{totalSteps}
          </span>
        </div>
        <div className="max-w-3xl mx-auto px-5 pb-3">
          <div className={`h-1.5 rounded-full ${variant === "dark" ? "bg-white/10" : "bg-stone-200"} overflow-hidden`}>
            <div
              className="h-full bg-brand-amber transition-all duration-700"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-6 py-8 md:py-12" id="main-content">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {title && (
            <div className="mb-8 text-center">
              <span className={`inline-block text-xs font-bold tracking-widest uppercase ${variant === "dark" ? "text-brand-amber" : "text-brand-amber"}`}>
                מדריך {step} מתוך {totalSteps}
              </span>
              <h1 className="mt-2 text-3xl sm:text-5xl font-[800] leading-tight">
                {title}
              </h1>
              {subtitle && (
                <p className={`mt-4 text-lg ${variant === "dark" ? "text-stone-300" : "text-stone-600"}`}>{subtitle}</p>
              )}
            </div>
          )}
          {typeof children === "function" ? children(lead) : children}
        </motion.div>
      </main>

      {/* Funnel slim footer with legal links */}
      <footer className={`mt-8 ${variant === "dark" ? "border-t border-white/10" : "border-t border-stone-200"}`}>
        <div className={`max-w-3xl mx-auto px-5 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs ${variant === "dark" ? "text-stone-400" : "text-stone-500"}`}>
          <span>© 2026 {lead.brand_name || "אמת או חובות"}</span>
          <div className="flex gap-4">
            <a
              href={lead.campaign_slug ? `/c/${lead.campaign_slug}/terms` : "/terms"}
              className="hover:underline"
              data-testid="funnel-footer-terms"
            >תקנון</a>
            <a
              href={lead.campaign_slug ? `/c/${lead.campaign_slug}/privacy` : "/privacy"}
              className="hover:underline"
              data-testid="funnel-footer-privacy"
            >מדיניות פרטיות</a>
            <a
              href={lead.campaign_slug ? `/c/${lead.campaign_slug}/contact` : "/contact"}
              className="hover:underline"
              data-testid="funnel-footer-contact"
            >יצירת קשר</a>
            <button
              type="button"
              onClick={() => window.openAccessibilityStatement && window.openAccessibilityStatement()}
              className="hover:underline"
              data-testid="funnel-footer-a11y"
            >הצהרת נגישות</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
